import { lspLanguageIdForPath, syntheticUri } from "./types.ts";

/** What the sync layer needs to know about an editor model (Monaco's ITextModel satisfies it through a thin adapter). */
export interface SyncDoc {
  path: string;
  getText(): string;
  /** Monotonic model version (Monaco: getVersionId()). */
  version(): number;
}

export interface SyncSink {
  notify(method: string, params: unknown): void;
}

/**
 * Keeps the language server's view of open documents equal to the Monaco buffers:
 *   open  → textDocument/didOpen    (exactly once per document per session)
 *   edit  → textDocument/didChange  (full text; unsaved buffers participate)
 *   save  → textDocument/didSave
 *   close → textDocument/didClose
 * Switching tabs never re-opens or re-sends anything: it is not an event of this class.
 */
export class DocumentSync {
  private readonly opened = new Map<string, { lspVersion: number }>();
  private readonly projectId: string;
  private readonly sink: SyncSink;

  constructor(projectId: string, sink: SyncSink) {
    this.projectId = projectId;
    this.sink = sink;
  }

  isOpen(path: string): boolean {
    return this.opened.has(path);
  }
  get openPaths(): string[] {
    return [...this.opened.keys()];
  }

  open(doc: SyncDoc): void {
    const languageId = lspLanguageIdForPath(doc.path);
    if (!languageId || this.opened.has(doc.path)) return;
    this.opened.set(doc.path, { lspVersion: 1 });
    this.sink.notify("textDocument/didOpen", {
      textDocument: { uri: syntheticUri(this.projectId, doc.path), languageId, version: 1, text: doc.getText() },
    });
  }

  change(doc: SyncDoc): void {
    const state = this.opened.get(doc.path);
    if (!state) return;
    state.lspVersion += 1;
    this.sink.notify("textDocument/didChange", {
      textDocument: { uri: syntheticUri(this.projectId, doc.path), version: state.lspVersion },
      contentChanges: [{ text: doc.getText() }],
    });
  }

  save(doc: SyncDoc): void {
    if (!this.opened.has(doc.path)) return;
    this.sink.notify("textDocument/didSave", {
      textDocument: { uri: syntheticUri(this.projectId, doc.path) },
      text: doc.getText(),
    });
  }

  close(path: string): void {
    if (!this.opened.delete(path)) return;
    this.sink.notify("textDocument/didClose", { textDocument: { uri: syntheticUri(this.projectId, path) } });
  }

  /** Forget everything WITHOUT notifying (the session is gone; a fresh session re-opens from scratch). */
  reset(): void {
    this.opened.clear();
  }
}
